import React, { FC } from 'react';
import {
  Box,
  ButtonAppearance,
  Icon,
  SideSheet,
  SideSheetBody,
  SideSheetClose,
  SideSheetContentCol,
  SideSheetFooter,
  SideSheetHeader,
  SideSheetSubHeader,
  Text,
  useModalState,
  theme,
} from '@bechtel/gds';
import { CommodityMilestone, CommodityStatusCode, CrewMember, MilestoneStatusCode, Weld } from '@bechtel/types';
import { FieldArray, Form, Formik, FormikProps } from 'formik';
import { noop } from 'lodash';
import { TitleValue, TextWithEllipsis } from '@bechtel/client-common';
import { AssignFooterButtonStyled, WeldAssignedStyled } from './styled';
import { t, Trans } from '@lingui/macro';
import { WeldStatusMessages } from '~/components/atoms/i18n-message-keys';

interface FormikState {
  assignedList: {
    commodityId: string;
    typeId: string;
    foreman: CrewMember;
  }[];
}

const initialFormikValues: FormikState = {
  assignedList: [],
};

interface WeldMilestoneAssignProps {
  onClose: () => void;
  milestone: CommodityMilestone;
  weldCommodity: Weld;
}

export const WeldMilestoneAssign: FC<WeldMilestoneAssignProps> = ({ onClose, milestone, weldCommodity }) => {
  const wp = weldCommodity.workPackage;
  const typeName = weldCommodity.type.name;
  const commodityTag = weldCommodity.tag;
  const { close: closeModal, open: openErrorModal } = useModalState(false);
  const welds = milestone.weldStatuses;

  return (
    <Formik initialValues={initialFormikValues} onSubmit={noop}>
      {({ values, submitForm }: FormikProps<FormikState>) => (
        <Form>
          <SideSheet visible onDismiss={onClose} style={{ width: `${theme.breakpoints.mobile}` }}>
            <SideSheetHeader title={t`Assign/Unassign Foreman`}>
              <SideSheetClose onClick={onClose} />
            </SideSheetHeader>
            <SideSheetSubHeader>
              <TitleValue title={t`Work Package`} value={wp.tag} data-testid="wpTag"></TitleValue>
              <TitleValue title={typeName} value={commodityTag} data-testid="commodityType"></TitleValue>
            </SideSheetSubHeader>

            <SideSheetBody>
              <SideSheetContentCol>
                <FieldArray name="assignedList">
                  {(arrayHelpers) => {
                    return (
                      <>
                        {welds &&
                          welds.map((item) => {
                            const { id, tag, type, status } = item;
                            return (
                              <WeldAssignedStyled key={id} data-testid={`assignee-${id}`}>
                                <Box
                                  pb={{ sm: '500' }}
                                  pt={{ sm: '400' }}
                                  justifyContent={{ sm: 'space-between' }}
                                  alignItems={{ sm: 'center' }}
                                  css={`
                                    min-width: 0; /* required to work flex-basis, if removed flex-basis will not truncate text with ellipses.*/
                                  `}
                                >
                                  <Box alignItems={{ sm: 'center' }} mr={{ sm: '300' }}>
                                    <Text size="400" weight="600" color="grey.1000" family="graphik">
                                      <Trans> Weld:</Trans>
                                    </Text>
                                    <Box ml={{ sm: '200' }}>
                                      <TextWithEllipsis
                                        size="400"
                                        color="grey.1000"
                                        family="graphik"
                                        data-testid="weldInfo"
                                      >
                                        {tag}
                                      </TextWithEllipsis>
                                    </Box>
                                  </Box>
                                  {status?.status ===
                                    (CommodityStatusCode.VT_APPROVED || CommodityStatusCode.CLAIMED) && (
                                    <Box
                                      alignItems={{ sm: 'center' }}
                                      css={`
                                        min-width: 0; /* required to work flex-basis, if removed flex-basis will not truncate text with ellipses.*/
                                      `}
                                    >
                                      <Box mr={{ sm: '300' }}>
                                        <Icon icon="check" />
                                      </Box>
                                      {status.status && (
                                        <TextWithEllipsis size="300" color="grey.1000">
                                          <Trans id={WeldStatusMessages[status.status].id} />
                                        </TextWithEllipsis>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              </WeldAssignedStyled>
                            );
                          })}
                      </>
                    );
                  }}
                </FieldArray>
              </SideSheetContentCol>
            </SideSheetBody>
            <SideSheetFooter>
              <Box justifyContent={{ sm: 'flex-end' }}>
                <Box mr={{ sm: '400' }}>
                  <AssignFooterButtonStyled appearance={ButtonAppearance.SECONDARY} onClick={onClose}>
                    <Trans> Cancel</Trans>
                  </AssignFooterButtonStyled>
                </Box>
                <Box>
                  <AssignFooterButtonStyled
                    data-testid="confirm"
                    onClick={submitForm}
                    appearance={ButtonAppearance.SECONDARY}
                  >
                    <Trans>Confirm</Trans>
                  </AssignFooterButtonStyled>
                </Box>
              </Box>
            </SideSheetFooter>
          </SideSheet>
        </Form>
      )}
    </Formik>
  );
};
